/**
 * Dollar Input with Frequency Select Renderer
 *
 * Dollar amount input with a dropdown to select frequency (Year or Month)
 * Stores the value and frequency separately in form data
 */

import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import type { FieldRenderer } from '@/MyCode/components/JsonFormSteps';

/**
 * Dollar Input with Frequency Select Renderer
 * Features:
 * - Dollar sign prefix
 * - Number input with comma formatting
 * - Frequency dropdown (Year/Month)
 * - Stores value and frequency as separate fields or combined object
 */
export const renderDollarInputWithFrequency: FieldRenderer = ({
    field,
    value,
    hasError,
    handleChange,
    formData,
    isRequired
}) => {
    // Get frequency field name (defaults to fieldName + '_frequency')
    const frequencyFieldName = (field as any).frequencyField || `${field.name}_frequency`;

    // Get current values - handle both object format {amount, frequency} and separate fields
    let dollarValue: number = 0;
    let frequency: string = 'Year';

    if (typeof value === 'object' && value !== null && 'amount' in value) {
        // Value is an object with amount and frequency
        dollarValue = value.amount || 0;
        frequency = value.frequency || 'Year';
    } else if (typeof value === 'number') {
        // Value is just a number, check formData for frequency
        dollarValue = value;
        frequency = formData?.[frequencyFieldName] || 'Year';
    } else {
        // Default or empty
        dollarValue = 0;
        frequency = formData?.[frequencyFieldName] || 'Year';
    }

    // Format number with commas
    const formatNumberWithCommas = (numValue: number | string): string => {
        if (!numValue || numValue === '') return '';
        const numStr = typeof numValue === 'string'
            ? numValue.replace(/\D/g, '')
            : numValue.toString();
        return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const [displayValue, setDisplayValue] = useState<string>(() => {
        if (dollarValue !== undefined && dollarValue !== null && dollarValue !== '') {
            const numVal = typeof dollarValue === 'number' ? dollarValue : Number(dollarValue);
            if (!isNaN(numVal)) {
                return formatNumberWithCommas(numVal);
            }
        }
        return '';
    });

    // Update display value when external value changes
    useEffect(() => {
        let currentDollarValue: number = 0;
        if (typeof value === 'object' && value !== null && 'amount' in value) {
            currentDollarValue = value.amount || 0;
        } else if (typeof value === 'number') {
            currentDollarValue = value;
        }

        if (currentDollarValue !== undefined && currentDollarValue !== null) {
            const numVal = typeof currentDollarValue === 'number' ? currentDollarValue : Number(currentDollarValue);
            if (!isNaN(numVal)) {
                setDisplayValue(formatNumberWithCommas(numVal));
            } else {
                setDisplayValue('');
            }
        } else {
            setDisplayValue('');
        }
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        const numericOnly = inputValue.replace(/\D/g, '');
        const numeric = numericOnly ? parseInt(numericOnly, 10) : 0;

        const formatted = formatNumberWithCommas(inputValue);
        setDisplayValue(formatted);

        // Store as object with amount and frequency
        handleChange(field.name, { amount: numeric, frequency });
    };

    const handleFrequencyChange = (newFrequency: string) => {
        // Update the main field with object format {amount, frequency}
        handleChange(field.name, { amount: dollarValue, frequency: newFrequency });
    };

    return (
        <div className="space-y-2 w-full">
            <Label htmlFor={field.name}>
                {field.label}
                {isRequired && <span className="text-destructive ml-1">*</span>}
            </Label>
            <div>
                <div className="relative flex rounded-md shadow-xs">
                    <span className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-sm text-muted-foreground z-10">
                        $
                    </span>
                    <Input
                        id={field.name}
                        type="text"
                        value={displayValue}
                        onChange={handleInputChange}
                        placeholder={field.placeholder || '0'}
                        className={`ps-8 -me-px rounded-e-none shadow-none focus-visible:z-10 ${hasError ? 'border-destructive' : ''}`}
                    />
                    <span className="pointer-events-none flex items-center justify-center px-2 text-sm text-muted-foreground border-y border-input bg-background">
                        /
                    </span>
                    <select
                        value={frequency}
                        onChange={(e) => handleFrequencyChange(e.target.value)}
                        className="w-fit rounded-s-none text-muted-foreground shadow-none hover:text-foreground border border-input bg-background px-3 py-2s text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                        <option value="Year">Year</option>
                        <option value="Month">Month</option>
                    </select>
                </div>
                {field.description && (
                    <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
                )}
            </div>
        </div>
    );
};

export default renderDollarInputWithFrequency;

